import { SIGNAL_TYPE_MAP } from '../constants/signalMaps';
import { BarInterval, RankingAlgorithm, ScannerSignalType, MonitorStatus, RegenerateStatus, PositionAction, ComparisonOperator, OrderSide } from '@/services/enums';

/**
 * K线相关类型定义
 */

export interface RankingAlgorithmConfig {
  type: RankingAlgorithm;
  parameters?: Record<string, number>;
}

export interface KlineCandle {
  time: number; // 秒级时间戳
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  changePercent?: number; // 涨跌幅 (可选)
  amount?: number; // 成交额 (可选，股票常用)
}

export interface KlineData {
  symbol: string;
  bar: BarInterval;
  candles: KlineCandle[];
  count: number;
}

export interface SignalScannerRequest {
  startTime: number; // 开始时间戳（秒）
  endTime: number; // 结束时间戳（秒）
  bar?: BarInterval; // 时间粒度
  signalType?: ScannerSignalType; // 信号类型
  rsiPeriod?: number; // RSI周期
  lookbackWindow?: number; // 回看窗口
  priceSlopeMin?: number; // 价格斜率最小值
  priceSlopeMax?: number; // 价格斜率最大值
  rsiSlopeMin?: number; // RSI斜率最小值（可选）
  rsiSlopeMax?: number; // RSI斜率最大值（可选）
  volumeSlopeMin?: number; // 成交量斜率最小值（可选）
  volumeSlopeMax?: number; // 成交量斜率最大值（可选）
  maxVolatility?: number; // 最大波动率（0.05表示5%）
  maxGapHours?: number; // 信号合并最大间隔（小时）
  obvSlopeMin?: number; // OBV斜率最小值（可选）
  priceAboveEma?: boolean; // 是否要求价格在EMA之上（可选）
  rsiMin?: number; // RSI最小值（可选，默认0，表示不限制）
  rsiMax?: number; // RSI最大值（可选，默认100，表示不限制）
  bbWidthMax?: number; // 布林带宽度最大值（可选，默认0.15）
  volumeRatioMin?: number; // 成交量比率最小值（可选，默认1.5）
}

export interface SignalScannerResult {
  symbol: string;
  startTime: number;
  endTime: number;
  closePrice: number; // 回看窗口最后一根K线的close价格
  result: {
    signalType: ScannerSignalType;
    priceSlope: number;
    rsiSlope: number;
    volumeSlope: number;
    obvSlope: number; // OBV斜率
    rsiValue: number;
    reason: string;
  };
}

export interface SignalScannerResponse {
  success: boolean;
  data: {
    signals: SignalScannerResult[];
    total: number;
  };
}

export interface MonitorSignalCondition {
  field: string;
  operator: ComparisonOperator;
  value: number;
}

export interface MonitorSignalRule {
  signalType?: string;
  confidence?: number;
  description: string;
  takeProfitPrice?: number;
  stopLossPrice?: number;
  conditions: Array<MonitorSignalCondition>;
}

export interface PositionSuggestion {
  action: PositionAction;
  reason: string;
  suggestedStopPrice?: number;
  suggestedTakeProfitPrice?: number;
}

export interface MonitorSignalConditionResult extends MonitorSignalCondition {
  met?: boolean; // 条件是否已满足（由后端检查后设置）
  currentValue?: number | boolean; // 当前值（由后端检查后设置，对于 cross_up/cross_down 为布尔值）
  ema20?: number; // EMA20值（仅当field为price时设置）
}

export interface MonitorSignal extends Omit<MonitorSignalRule, 'conditions'> {
  type: OrderSide;
  conditions: Array<MonitorSignalConditionResult>;
  triggered: boolean;
  triggeredAt?: string;
}

export interface BaseKlineIndicators {
  sma50: number | null;
  ema20: number | null;
  ema50: number | null;
  ema200: number | null;
  rsi14: number | null;
  macd: {
    macd: number | null;
    signal: number | null;
    histogram: number | null;
  };
  bb: {
    upper: number | null;
    middle: number | null;
    lower: number | null;
  };
  atr: number | null;
  obv_slope: number | null;
  vwap: number | null;
}

export interface MonitorIndicators extends BaseKlineIndicators {
  price?: number;
  close?: number;
  high?: number;
  low?: number;
}

export interface MonitorRulesResultDto {
  buySignals?: Array<MonitorSignalRule>;
  sellSignals?: Array<MonitorSignalRule>;
  positionSuggestion?: PositionSuggestion | null;
}

/**
 * 生成信号类型的 Tag (与后端逻辑一致：去除非字母数字，截取前16位)
 */
export function generateTagFromSignalType(signalType: string): string {
  if (!signalType) return 'ai';
  const cleaned = signalType.replace(/[^a-zA-Z0-9]/g, '');
  return cleaned.substring(0, 16) || 'ai';
}

/**
 * 获取信号类型的中文描述
 * 支持完整信号类型 (如 "Trend Reversal") 和 后端生成的 Tag (如 "TrendRevers")
 */
export function getSignalLabel(typeOrTag: string | null | undefined): string {
  if (!typeOrTag) return '-';

  // 1. 尝试直接匹配完整 Key
  if (SIGNAL_TYPE_MAP[typeOrTag]) {
    return SIGNAL_TYPE_MAP[typeOrTag];
  }

  // 2. 尝试匹配 Tag
  // 遍历所有定义的信号类型，计算其 Tag 并比对
  for (const [fullType, label] of Object.entries(SIGNAL_TYPE_MAP)) {
    const tag = generateTagFromSignalType(fullType);
    // 如果输入的 typeOrTag 等于计算出的 tag
    if (tag === typeOrTag) {
      return label;
    }
  }

  // 3. 兜底：如果本身就是中文或未定义的，直接返回
  return typeOrTag;
}

export const CONDITION_FIELD_MAP: Record<string, string> = {
  price: '价格',
  close: '收盘价',
  high: '最高价',
  low: '最低价',
  rsi14: 'RSI(14)',
  macd: 'MACD',
  macd_signal: 'MACD信号',
  macd_histogram: 'MACD柱状图',
  macd_divergence: 'MACD顶背离',
  macd_bottom_divergence: 'MACD底背离',
  macd_cross: 'MACD交叉',
  volume_decline: '成交量萎缩',
  price_new_high: '价格创新高',
  volume_surge_bullish: '放量阳线',
  volume: '成交量',
  volume_ratio: '成交量比例',
  obv_slope: 'OBV斜率',
  ema20: 'EMA(20)',
  ema50: 'EMA(50)',
  sma50: 'SMA(50)',
  ema20_cross: 'EMA20交叉',
  bbUpper: '布林带上轨',
  bbLower: '布林带下轨',
  bbMiddle: '布林带中轨',
};

export const CONDITION_OPERATOR_MAP: Record<string, string> = {
  '>': '>',
  '<': '<',
  '>=': '≥',
  '<=': '≤',
  '==': '=',
  '=': '=',
  'cross_up': '金叉',
  'cross_down': '死叉',
};

export const formatCondition = (condition: any) => {
  const fieldName = CONDITION_FIELD_MAP[condition.field] || condition.field;
  const operator = CONDITION_OPERATOR_MAP[condition.operator] || condition.operator;

  if (condition.operator === 'cross_up' || condition.operator === 'cross_down') {
    return `${fieldName} ${operator}`;
  }
  return `${fieldName} ${operator} ${condition.value}`;
};

export interface MonitorStatusUpdateFields {
  status?: MonitorStatus;
  regenerateStatus?: RegenerateStatus;
  statusReason?: string; // 状态原因说明
  enableAutoTrading?: boolean;
  tradingParams?: {
    leverage?: number;
    positionSize?: number;
    takeProfitPercent?: number;
    stopLossPercent?: number;
  };
}
